import React, {
  ComponentProps,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ITEM_COLORS } from '../../constants';
import { BlurItem } from '../../items/blur/blur-item-type';
import { getGroupItemLabel } from '../../items/group/group-label';
import type { GroupItem } from '../../items/group/group-item-type';
import { EditorStarterItem } from '../../items/item-type';
import { SolidItem } from '../../items/solid/solid-item-type';
import { useAllItems } from '../../utils/use-context';
import { cn } from '@crabs-ai/ds-react';
import {
  EditorStarterAsset,
  isCaptionAsset,
} from '@/features/remotion-editor/editor/assets/assets';
import { useLocalUrls } from '@/features/remotion-editor/editor/caching/load-to-blob-url';
import { useAssetFrames } from '@/features/remotion-editor/editor/contexts/asset-frames-context';
import {
  getCachedGifFirstFrame,
  getGifFirstFrame,
  isAnimatedImageMimeType,
} from '@/features/remotion-editor/editor/caching/extract-gif-first-frame';

const SimplePreview = ({
  children,
  style,
  className,
}: ComponentProps<'div'>) => {
  return (
    <div
      style={{ background: '#FF9843', ...style }}
      className={cn(
        'flex h-full w-full flex-nowrap gap-1 p-1 text-xs text-sys-cont-inverse-dark',
        className,
      )}
    >
      {children}
    </div>
  );
};

const Text = ({ children }: PropsWithChildren) => {
  return <span className="truncate">{children}</span>;
};

SimplePreview.Text = Text;

const CaptionsPreview: React.FC<{ text: string }> = ({ text }) => {
  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.captions }}
      className="items-center text-comp-moderate-xsmall! text-sys-cont-inverse-dark!"
    >
      <SimplePreview.Text>{text ?? 'Caption'}</SimplePreview.Text>
    </SimplePreview>
  );
};

const TextItemPreview: React.FC<{
  text: string;
}> = ({ text }) => {
  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.text }}
      className="items-center text-comp-moderate-xsmall! text-sys-cont-inverse-dark!"
    >
      <SimplePreview.Text>{text}</SimplePreview.Text>
    </SimplePreview>
  );
};

const useStaticFirstFrameUrl = ({
  assetId,
  localUrl,
  enabled,
}: {
  assetId: string | undefined;
  localUrl: string | null | undefined;
  enabled: boolean;
}) => {
  const [staticUrl, setStaticUrl] = useState<string | null>(null);
  const [isFirstFrameResolved, setIsFirstFrameResolved] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setStaticUrl(null);
      setIsFirstFrameResolved(true);
      return;
    }

    if (!assetId || !localUrl) {
      setStaticUrl(null);
      setIsFirstFrameResolved(false);
      return;
    }

    const cached = getCachedGifFirstFrame(assetId);
    if (cached) {
      setStaticUrl(cached);
      setIsFirstFrameResolved(true);
      return;
    }

    setStaticUrl(null);
    setIsFirstFrameResolved(false);
    let cancelled = false;

    getGifFirstFrame(assetId, localUrl).then((url) => {
      if (!cancelled) {
        setStaticUrl(url);
        setIsFirstFrameResolved(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [assetId, enabled, localUrl]);

  if (!enabled) {
    return localUrl ?? null;
  }

  return staticUrl ?? (isFirstFrameResolved ? (localUrl ?? null) : null);
};

const ImageItemPreview: React.FC<{ asset: EditorStarterAsset | null }> = ({
  asset,
}) => {
  const localUrls = useLocalUrls();
  const localUrl = asset ? localUrls[asset.id] : null;
  const displayUrl = useStaticFirstFrameUrl({
    assetId: asset?.id,
    localUrl,
    enabled: isAnimatedImageMimeType(asset?.mimeType),
  });
  const fallbackUrl = displayUrl ?? asset?.remoteUrl ?? null;

  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.image }}
      className="items-center overflow-hidden p-0! text-sys-cont-inverse-dark"
    >
      {fallbackUrl && (
        <img
          src={fallbackUrl}
          className="h-full w-auto shrink-0 object-cover opacity-50"
          alt=""
        />
      )}
    </SimplePreview>
  );
};

const GifItemPreview: React.FC<{ asset: EditorStarterAsset | null }> = ({
  asset,
}) => {
  const localUrls = useLocalUrls();
  const localUrl = asset ? localUrls[asset.id] : null;
  const displayUrl = useStaticFirstFrameUrl({
    assetId: asset?.id,
    localUrl,
    enabled: true,
  });
  const fallbackUrl = displayUrl ?? asset?.remoteUrl ?? null;

  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.gif }}
      className="items-center overflow-hidden p-0! text-sys-cont-inverse-dark"
    >
      {fallbackUrl && (
        <img
          src={fallbackUrl}
          className="h-full w-auto shrink-0 object-cover opacity-50"
          alt=""
        />
      )}
    </SimplePreview>
  );
};

const VideoItemPreview: React.FC<{ asset: EditorStarterAsset | null }> = ({
  asset,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { frames } = useAssetFrames(asset?.id ?? '');
  const firstFrame = frames[0] ?? null;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !firstFrame) return;

    const bitmap = firstFrame.imageBitmap;
    // 썸네일용으로 축소하여 메모리 절약 (고해상도 소스 그대로 사용 방지)
    const MAX_THUMB = 160;
    const scale = Math.min(
      MAX_THUMB / bitmap.width,
      MAX_THUMB / bitmap.height,
      1,
    );
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  }, [firstFrame]);

  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.video }}
      className="relative overflow-hidden text-sys-cont-inverse-dark"
    >
      {firstFrame && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
      )}
    </SimplePreview>
  );
};

const AudioItemPreview: React.FC = () => {
  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.audio }}
      className="text-sys-cont-inverse-dark"
    ></SimplePreview>
  );
};

const SolidItemPreview: React.FC<{
  item: SolidItem;
}> = ({ item }) => {
  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.solid }}
      className="items-center text-comp-moderate-xsmall! text-sys-cont-inverse-dark!"
    >
      <div
        className={'w- mt-[1px] h-2 shrink-0 rounded-full'}
        style={{
          backgroundColor: item.color,
        }}
      ></div>
      <SimplePreview.Text>Solid</SimplePreview.Text>
    </SimplePreview>
  );
};

const BlurItemPreview: React.FC<{
  item: BlurItem;
}> = ({ item }) => {
  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.blur }}
      className="items-center text-comp-moderate-xsmall! text-sys-cont-inverse-dark!"
    >
      <div
        className={'mt-[1px] h-2 w-2 shrink-0 rounded-full'}
        style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: `blur(${Math.min(item.blurAmount, 3)}px)`,
        }}
      ></div>
      <SimplePreview.Text>Blur</SimplePreview.Text>
    </SimplePreview>
  );
};

const GroupItemPreview: React.FC<{
  item: GroupItem;
}> = ({ item }) => {
  const { items } = useAllItems();
  // 멤버 텍스트로 대표 라벨을 만들고, 없으면 기존 '그룹 (n)' 표기로 폴백한다.
  const label = getGroupItemLabel(item, items);

  return (
    <SimplePreview
      style={{ background: ITEM_COLORS.group }}
      className="items-center text-comp-moderate-xsmall! text-sys-cont-inverse-dark!"
    >
      <SimplePreview.Text>
        {label ?? `그룹 (${item.children.length})`}
      </SimplePreview.Text>
    </SimplePreview>
  );
};

export const TimelineItemPreview: React.FC<{
  item: EditorStarterItem;
  asset: EditorStarterAsset | null;
}> = ({ item, asset }) => {
  if (item.type === 'text') {
    return <TextItemPreview text={item.text} />;
  }

  if (item.type === 'image') {
    return <ImageItemPreview asset={asset} />;
  }

  if (item.type === 'video') {
    return <VideoItemPreview asset={asset} />;
  }

  if (item.type === 'solid') {
    return <SolidItemPreview item={item} />;
  }

  if (item.type === 'blur') {
    return <BlurItemPreview item={item} />;
  }

  if (item.type === 'captions' && asset && isCaptionAsset(asset)) {
    return (
      <CaptionsPreview
        text={asset.captions.map((caption) => caption.text).join(' ')}
      />
    );
  }

  if (item.type === 'audio') {
    return <AudioItemPreview />;
  }

  if (item.type === 'gif') {
    return <GifItemPreview asset={asset} />;
  }

  if (item.type === 'webp') {
    return <GifItemPreview asset={asset} />;
  }

  if (item.type === 'code') {
    return (
      <SimplePreview color="#7c3aed">
        <SimplePreview.Text>{item.title || 'Code'}</SimplePreview.Text>
      </SimplePreview>
    );
  }

  if (item.type === 'pdf' || item.type === 'ppt') {
    return (
      <SimplePreview color="#0f766e">
        <SimplePreview.Text>
          {(asset?.filename || item.type.toUpperCase()) +
            ` · p.${item.pageIndex + 1}`}
        </SimplePreview.Text>
      </SimplePreview>
    );
  }

  if (item.type === 'group') {
    return <GroupItemPreview item={item} />;
  }

  throw new Error(`Unknown item type: ${JSON.stringify(item as never)}`);
};
